/* eslint-disable @typescript-eslint/no-empty-interface */
import type { GlobalAttributesT, XstyledProps, DSPropTypesSchema, ValidationMap } from '@elliemae/ds-props-helpers';
import {
  PropTypes,
  getPropsPerSlotPropTypes,
  globalAttributesPropTypes,
  xstyledPropTypes,
} from '@elliemae/ds-props-helpers';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { DSFloatingWrapperName, DSFloatingWrapperSlots } from '../../constants/index.js';

export declare namespace DSFloatingWrapperT {
  export interface RequiredProps {
    children: TypescriptHelpersT.ReactChildrenComplete;
    innerRef: TypescriptHelpersT.AnyRef<HTMLDivElement>;
    isOpen: boolean;
    floatingStyles: React.CSSProperties;
  }

  export interface DefaultProps {
    context: {
      portalDOMContainer?: HTMLElement;
      withoutPortal: boolean;
      animationDuration: number;
      withoutAnimation: boolean;
    };
    customOffset: [number, number];
  }

  export interface OptionalProps
    extends TypescriptHelpersT.PropsForGlobalOnSlots<typeof DSFloatingWrapperName, typeof DSFloatingWrapperSlots> {
    onAnimationEnd?: React.AnimationEventHandler<HTMLDivElement>;
    onAnimationStartTriggered?: () => void;
  }

  export interface Props
    extends Partial<DefaultProps>,
      RequiredProps,
      OptionalProps,
      Omit<
        GlobalAttributesT<HTMLElement>,
        keyof DefaultProps | keyof OptionalProps | keyof RequiredProps | keyof XstyledProps
      >,
      XstyledProps {}

  export interface InternalProps
    extends DefaultProps,
      RequiredProps,
      OptionalProps,
      Omit<
        GlobalAttributesT<HTMLElement>,
        keyof DefaultProps | keyof OptionalProps | keyof RequiredProps | keyof XstyledProps
      >,
      XstyledProps {}
}

export const defaultProps: Partial<DSFloatingWrapperT.DefaultProps> = {
  context: {
    withoutPortal: false,
    animationDuration: 300,
    withoutAnimation: false,
  },
  customOffset: [0, 12],
};

export const DSFloatingWrapperPropTypes: DSPropTypesSchema<DSFloatingWrapperT.InternalProps> = {
  ...getPropsPerSlotPropTypes(DSFloatingWrapperName, DSFloatingWrapperSlots),
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  children: PropTypes.node.description('Content of the floating wrapper').isRequired,
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).description('Ref for the floating wrapper')
    .isRequired,
  isOpen: PropTypes.bool.description('Whether the floating wrapper is open').isRequired,
  floatingStyles: PropTypes.object.description('Style for the floating wrapper').isRequired,
  context: PropTypes.shape({
    portalDOMContainer: PropTypes.instanceOf(HTMLElement)
      .description('The DOM element where the tooltip will be rendered.')
      .defaultValue('the first "main" landmark available in the document or the body if no "main" is found'),
    withoutPortal: PropTypes.bool.description('Whether to render the floating wrapper without a portal'),
    animationDuration: PropTypes.number.description('Duration of the animation'),
    withoutAnimation: PropTypes.bool.description('Whether to render the floating wrapper without animation'),
  }).description('Context for the floating wrapper'),
  onAnimationStartTriggered: PropTypes.func.description(
    'Callback invoked when the component trigger the animation start. Required to properly position nested floating context without visual artefacts in case animations are used.',
  ),
  onAnimationEnd: PropTypes.func.description(
    'Callback when the animation ends. Required to properly position nested floating context without visual artefacts in case animations are used.',
  ),
  customOffset: PropTypes.arrayOf(PropTypes.number).description('Custom offset for the floating wrapper'),
};

export const DSFloatingWrapperPropTypesSchema =
  DSFloatingWrapperPropTypes as unknown as ValidationMap<DSFloatingWrapperT.Props>;
